import { EditorContent } from '@tiptap/react';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import '../../../pages/AdminSupportInboxPage/styles/composerEditor.css';
import { useLocalStorageState } from '../../../../hooks/useLocalStorage';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { TicketPriority } from '../../../../models/Support/SupportEnums';
import { isValidEmail } from '../../../../models/utils/validationUtils';
import { useAdminAccountQuery } from '../../../../queries/Admin/Account/useAdminAccountQuery';
import { useMyAgentConfigQuery } from '../../../../queries/Support/useSupportAgentConfig';
import { useSupportCategoriesQuery } from '../../../../queries/Support/useSupportCategories';
import { useSupportMailboxListQuery } from '../../../../queries/Support/useSupportMailboxQuery';
import { useCreateTicketMutation } from '../../../../queries/Support/useSupportTicketMutations';
import { getApiErrorMessage } from '../../../../utils/Support/supportHelpers';
import { ComposerAttachmentList } from '../../../pages/AdminSupportInboxPage/TicketComposer/ComposerAttachmentList';
import { useComposerAttachments } from '../../../pages/AdminSupportInboxPage/TicketComposer/useComposerAttachments';
import { useComposerInlineImages } from '../../../pages/AdminSupportInboxPage/TicketComposer/useComposerInlineImages';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { buildAdminSupportTicketUrl } from '../../../utils';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import { LAST_MAILBOX_STORAGE_KEY } from '../supportStorageKeys';
import { AccountField } from './AccountField';
import { ContactField } from './ContactField';
import {
	getNewTicketDraftStorageKey,
	NewTicketDraftAccount,
	NewTicketDraftContact,
	readStoredNewTicketDraft,
	useNewTicketDraft,
} from './newTicketDraft';
import { newTicketStyles as s } from './styles';
import { useDescriptionEditor } from './useDescriptionEditor';

const PRIORITY_OPTIONS = [TicketPriority.Low, TicketPriority.Normal, TicketPriority.High, TicketPriority.Urgent];
const NO_CATEGORY = 'none';

export interface NewTicketModalProps {
	/** Prefills the account (and its CSM) — set when launched from the account details page or an account-scoped inbox. */
	defaultAccount?: NewTicketDraftAccount;
	defaultMailboxId?: string;
	onClose: () => void;
	/** When provided the host owns post-create navigation; otherwise the success toast links to the ticket. */
	onCreated?: (ticket: ISupportTicket) => void;
}

/**
 * Submit-a-ticket modal for the support desk. Every field change is saved to a per-agent local
 * draft (debounced), so the modal can be closed and reopened without losing work; the draft is
 * cleared on successful submission.
 */
export const NewTicketModal: React.FC<NewTicketModalProps> = ({
	defaultAccount,
	defaultMailboxId,
	onClose,
	onCreated,
}) => {
	const userSession = useUserSession();
	const storageKey = getNewTicketDraftStorageKey(userSession.user?.id ?? '', defaultAccount?.id);
	const [initialDraft] = React.useState(() => readStoredNewTicketDraft(storageKey));

	const [subject, setSubject] = React.useState(initialDraft?.subject ?? '');
	const [descriptionHtml, setDescriptionHtml] = React.useState(initialDraft?.descriptionHtml ?? '');
	const [priority, setPriority] = React.useState<TicketPriority>(initialDraft?.priority ?? TicketPriority.Normal);
	const [categoryId, setCategoryId] = React.useState(initialDraft?.categoryId ?? '');
	const [account, setAccount] = React.useState<NewTicketDraftAccount | null>(
		initialDraft?.account ?? defaultAccount ?? null
	);
	const [contact, setContact] = React.useState<NewTicketDraftContact | null>(initialDraft?.contact ?? null);
	const [manualContact, setManualContact] = React.useState(initialDraft?.manualContact ?? false);
	const [selectedMailboxId, setSelectedMailboxId] = React.useState(initialDraft?.mailboxId ?? defaultMailboxId ?? '');
	const [error, setError] = React.useState('');
	const [manualEmailError, setManualEmailError] = React.useState('');

	const { data: mailboxes } = useSupportMailboxListQuery();
	const mailboxList = mailboxes ?? [];
	const [storedMailboxId] = useLocalStorageState<string>(LAST_MAILBOX_STORAGE_KEY, '');
	const isKnownMailbox = (id: string) => !!id && mailboxList.some(m => m.id === id);
	const fallbackMailboxId = mailboxList.find(m => m.isActive)?.id ?? mailboxList[0]?.id ?? '';
	const mailboxId = isKnownMailbox(selectedMailboxId)
		? selectedMailboxId
		: isKnownMailbox(storedMailboxId)
			? storedMailboxId
			: fallbackMailboxId;

	const categoriesQuery = useSupportCategoriesQuery(mailboxId);
	const categories = categoriesQuery.data?.values ?? [];
	const validCategoryId = categories.some(category => category.id === categoryId) ? categoryId : '';

	// Mailboxes can be visible through role claims without an agent membership, and only agents may
	// create tickets — surface that before submit instead of letting the server 403.
	const myAgentConfigQuery = useMyAgentConfigQuery(mailboxId);
	const isNotAnAgent = myAgentConfigQuery.data?.isAgent === false;

	// The CSM lives on the account (the ticket inherits it server-side); shown so the submitter
	// knows who will own the follow-up. Errors stay silent — the row just shows the fallback.
	const accountQuery = useAdminAccountQuery({ accountId: account?.id ?? '', enabled: !!account?.id });
	const csmName = accountQuery.data?.additionalInfo?.customerSuccessName;

	const { clearDraft } = useNewTicketDraft({
		storageKey,
		subject,
		descriptionHtml,
		priority,
		categoryId: validCategoryId,
		mailboxId,
		account,
		contact,
		manualContact,
	});

	const createTicket = useCreateTicketMutation();
	const attachments = useComposerAttachments();

	// insertDescriptionFiles is declared below (it needs the editor); the callbacks only run on
	// paste/drop, long after render, so the binding is initialized by then.
	const editor = useDescriptionEditor(
		{
			insertFiles: (files, insertAt) => insertDescriptionFiles(files, insertAt),
			onUpdate: instance => setDescriptionHtml(instance.isEmpty ? '' : instance.getHTML()),
		},
		initialDraft?.descriptionHtml ?? ''
	);
	const inlineImages = useComposerInlineImages(editor);
	const hasUploading = attachments.hasUploading || inlineImages.hasUploadingImages;

	// Same split as the ticket composer: images land inline in the description, other files attach.
	const insertDescriptionFiles = (files: File[], insertAt?: number) => {
		const imageFiles = files.filter(file => file.type.startsWith('image/'));
		const otherFiles = files.filter(file => !file.type.startsWith('image/'));
		if (imageFiles.length > 0) inlineImages.insertImages(imageFiles, insertAt);
		if (otherFiles.length > 0) attachments.addFiles(otherFiles);
	};

	const changeContact = (nextContact: NewTicketDraftContact | null, contactAccount?: NewTicketDraftAccount) => {
		setContact(nextContact);
		setManualEmailError('');
		if (contactAccount) setAccount(contactAccount);
	};

	// Pasting anywhere in the form takes clipboard files, so a screenshot can be dropped in without
	// focusing the description first. The editor consumes its own paste events (defaultPrevented),
	// so this never double-adds; text pastes pass through untouched.
	const handlePaste = (event: React.ClipboardEvent) => {
		if (event.defaultPrevented) return;
		const files = Array.from(event.clipboardData?.files ?? []);
		if (files.length === 0) return;
		event.preventDefault();
		insertDescriptionFiles(files);
	};

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;
		insertDescriptionFiles(Array.from(files));
		event.target.value = '';
	};

	const handleSubmit = () => {
		const trimmedSubject = subject.trim();
		const manualEmail = manualContact ? (contact?.email ?? '').trim() : '';
		if (!mailboxId) {
			setError('Select a mailbox for the ticket.');
			return;
		}
		if (!trimmedSubject) {
			setError('Subject is required.');
			return;
		}
		if (!descriptionHtml.trim()) {
			setError('Description is required.');
			return;
		}
		if (manualContact && manualEmail && !isValidEmail(manualEmail)) {
			setManualEmailError('Please enter a valid email address');
			return;
		}
		if (hasUploading) {
			setError('Attachments are still uploading — try again in a moment.');
			return;
		}
		setError('');
		setManualEmailError('');

		const payloadContact = manualContact
			? manualEmail
				? { email: manualEmail, name: contact?.name?.trim() || undefined }
				: undefined
			: (contact ?? undefined);

		createTicket.mutate(
			{
				subject: trimmedSubject,
				descriptionHtml,
				mailboxId,
				priority,
				categoryId: validCategoryId || undefined,
				accountId: account?.id,
				contact: payloadContact,
				attachmentIds: attachments.pendingAttachments.map(attachment => attachment.id),
			},
			{
				onSuccess: ({ ticket, warnings }) => {
					clearDraft();
					const toastOptions = onCreated
						? undefined
						: {
								action: {
									label: 'View ticket',
									onClick: () => window.open(buildAdminSupportTicketUrl(ticket.id), '_blank'),
								},
							};
					if (warnings.length > 0) {
						toast.warning(
							`Ticket #${ticket.number} was created, but we couldn't ${warnings.join(' or ')}. You can finish that from the ticket view.`,
							toastOptions
						);
					} else {
						toast.success(`Ticket #${ticket.number} created`, toastOptions);
					}
					onCreated?.(ticket);
					onClose();
				},
				onError: err => setError(getApiErrorMessage(err, 'Failed to create the ticket. Please try again.')),
			}
		);
	};

	return (
		<Dialog
			open
			onOpenChange={open => {
				if (!open) onClose();
			}}
		>
			<DialogPopup styles={[s.popup]}>
				<DialogHeader>
					<DialogTitle>New Support Ticket</DialogTitle>
					<DialogDescription>Your draft saves automatically — close and come back anytime.</DialogDescription>
				</DialogHeader>
				<div className={css(shared.modalBody)} onPaste={handlePaste}>
					{isNotAnAgent && (
						<div className={css(shared.modalErrorText)} role='status'>
							You're not an agent in this mailbox, so ticket creation is disabled.{' '}
							{mailboxList.length > 1
								? 'Pick another mailbox or ask a mailbox admin to add you to the team.'
								: 'Ask a mailbox admin to add you to the team.'}
						</div>
					)}
					{mailboxList.length > 1 && (
						<div>
							<Label styles={[shared.modalLabel]}>Mailbox</Label>
							<Select
								items={mailboxList.map(mailbox => ({ value: mailbox.id, label: mailbox.name }))}
								value={mailboxId}
								onValueChange={setSelectedMailboxId}
							>
								<SelectTrigger styles={s.selectTrigger}>
									<SelectValue placeholder='Select a mailbox' />
								</SelectTrigger>
								<SelectContent>
									{mailboxList.map(mailbox => (
										<SelectItem key={mailbox.id} value={mailbox.id}>
											{mailbox.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}
					<div>
						<Label styles={[shared.modalLabel]}>Account</Label>
						<AccountField account={account} onChange={setAccount} />
						{account && (
							<div className={css(s.csmRow)}>
								CSM: <span className={css(s.csmName)}>{csmName || 'Unassigned'}</span>
							</div>
						)}
					</div>
					<div>
						<Label styles={[shared.modalLabel]}>Contact</Label>
						<ContactField
							account={account}
							contact={contact}
							manualContact={manualContact}
							manualEmailError={manualEmailError}
							onChangeContact={changeContact}
							onToggleManual={manual => {
								setManualContact(manual);
								setContact(null);
								setManualEmailError('');
							}}
						/>
					</div>
					<div>
						<Label styles={[shared.modalLabel]}>Subject</Label>
						<Input
							styles={[shared.modalInput]}
							type='text'
							placeholder='Brief summary of the issue'
							value={subject}
							onChange={e => setSubject(e.target.value)}
						/>
					</div>
					<div>
						<Label styles={[shared.modalLabel]}>Description</Label>
						{/* biome-ignore lint/a11y/noStaticElementInteractions: clicking the frame's padding focuses the editor inside it. */}
						<div className={css(s.descriptionEditor)} onClick={() => editor?.commands.focus()}>
							<EditorContent editor={editor} />
						</div>
						<div className={css(s.attachRow)}>
							<Button variant='link' styles={[s.manualLink]} onClick={() => attachments.fileInputRef.current?.click()}>
								Attach screenshots or files
							</Button>
							<span className={css(s.draftHint)}>Paste or drop works too — images go inline, other files attach.</span>
						</div>
						<Input
							ref={attachments.fileInputRef}
							type='file'
							multiple
							styles={[s.fileInput]}
							onChange={handleFileSelect}
						/>
						<ComposerAttachmentList
							attachments={attachments.pendingAttachments}
							onRemove={attachments.removeAttachment}
						/>
						{attachments.uploadError && <div className={css(shared.modalErrorText)}>{attachments.uploadError}</div>}
						{inlineImages.imageUploadError && (
							<div className={css(shared.modalErrorText)}>{inlineImages.imageUploadError}</div>
						)}
					</div>
					<div className={css(s.fieldRow)}>
						<div>
							<Label styles={[shared.modalLabel]}>Priority</Label>
							<Select value={priority} onValueChange={value => setPriority(value as TicketPriority)}>
								<SelectTrigger styles={s.selectTrigger}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{PRIORITY_OPTIONS.map(option => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						{categories.length > 0 && (
							<div>
								<Label styles={[shared.modalLabel]}>Category</Label>
								<Select
									items={[
										{ value: NO_CATEGORY, label: 'None' },
										...categories.map(category => ({ value: category.id, label: category.name })),
									]}
									value={validCategoryId || NO_CATEGORY}
									onValueChange={value => setCategoryId(value === NO_CATEGORY ? '' : value)}
								>
									<SelectTrigger styles={s.selectTrigger}>
										<SelectValue placeholder='None' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value={NO_CATEGORY}>None</SelectItem>
										{categories.map(category => (
											<SelectItem key={category.id} value={category.id}>
												{category.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
					</div>
					{error && <div className={css(shared.modalErrorText)}>{error}</div>}
				</div>
				<DialogFooter>
					<Button variant='outline' size='sm' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleSubmit} disabled={createTicket.isPending || isNotAnAgent}>
						{createTicket.isPending ? 'Creating...' : 'Create Ticket'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
